import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BrandStorySection = () => {
  const storyPoints = [
  {
    icon: "Droplets",
    title: "The Purity",
    description: "Starting fresh in the market with a focus on delivering the purest water possible. Every bottle undergoes rigorous filtration and quality checks."
  },
  {
    icon: "Recycle",
    title: "The Innovation",
    description: "We create our water bottles from 100% recycled plastic materials, proving that environmental responsibility and quality can go hand in hand."
  },
  {
    icon: "Heart",
    title: "The Promise",
    description: "Though we're new, our commitment is strong - to provide pure, clean hydration that refreshes your moments and respects our planet."
  }];


  return (
    <section className="py-20 bg-surface">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16">

            <div className="flex items-center justify-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                <Icon name="BookOpen" size={24} color="white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-poppins font-bold text-text-primary">
                Our Brand Story
              </h2>
            </div>
            <p className="text-lg text-text-secondary font-inter max-w-3xl mx-auto leading-relaxed">
              Every great brand has a story. Ours begins with a simple mission: Why shouldn't pure water come in responsible packaging?
            </p>
          </motion.div>

          {/* Main Story Content */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8">

              <div className="space-y-6">
                <h3 className="text-2xl md:text-3xl font-poppins font-bold text-text-primary">
                  Sip the freshness, live the moment.
                </h3>
                <div className="space-y-4 text-text-secondary font-inter leading-relaxed">
                  <p>
                    "Sip the freshness, live the moment" is more than our tagline—it's our promise. As a new player in the beverage market, we're committed to delivering exceptional water purity that enhances every moment of your day.
                  </p>
                  <p>
                    Our orange logo represents the energy and vitality that comes with proper hydration, while our focus remains on the fundamentals: creating the purest, cleanest water using bottles made from recycled plastic materials.
                  </p>
                  <p>
                    We may be new to the market, but we're building something special—a brand that proves you don't need to compromise on quality or environmental responsibility to deliver refreshing hydration.
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4 p-6 bg-background rounded-xl liquid-shadow">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Quote" size={24} color="white" />
                </div>
                <div>
                  <p className="text-text-primary font-inter font-medium italic">
                    "Pure water. Pure moments. Purely Mava."
                  </p>
                  <p className="text-text-secondary text-sm mt-1">— Mava Brand Promise</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative">

              <div className="relative w-full h-96 rounded-2xl overflow-hidden liquid-shadow">
                <Image
                  src="/assets/images/mava-1762174454410.jpg"
                  alt="Mava Cold Beverages water bottles made from recycled plastic materials showcasing commitment to water purity and environmental responsibility"
                  className="w-full h-full object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-secondary/30 via-transparent to-transparent" />
                
                {/* Overlay Content */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-background/95 backdrop-blur-md rounded-xl p-4">
                    <div className="flex items-center space-x-3">
                      <Icon name="Droplets" size={20} color="var(--color-primary)" />
                      <span className="font-inter font-medium text-text-primary">
                        Water Purity Innovation
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Story Points Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {storyPoints?.map((point, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-background rounded-xl p-8 liquid-shadow hover:shadow-lg brand-transition group">

                <div className="space-y-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center group-hover:scale-110 brand-transition">
                    <Icon name={point?.icon} size={24} color="white" />
                  </div>
                  <h4 className="text-xl font-poppins font-bold text-text-primary">
                    {point?.title}
                  </h4>
                  <p className="text-text-secondary font-inter leading-relaxed">
                    {point?.description}
                  </p>
                </div>
              </motion.div>
            )}
          </div>

          {/* Brand Values */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-20 text-center">

            <div className="bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 rounded-2xl p-8 md:p-12">
              <h3 className="text-2xl md:text-3xl font-poppins font-bold text-text-primary mb-6">
                Our Core Values
              </h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="space-y-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto">
                    <Icon name="Droplets" size={20} color="white" />
                  </div>
                  <h4 className="font-poppins font-semibold text-text-primary">Purity</h4>
                  <p className="text-text-secondary font-inter text-sm">
                    Uncompromising water quality in every bottle
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto">
                    <Icon name="Recycle" size={20} color="white" />
                  </div>
                  <h4 className="font-poppins font-semibold text-text-primary">Recycled Materials</h4>
                  <p className="text-text-secondary font-inter text-sm">
                    100% recycled plastic bottles for a cleaner future
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto">
                    <Icon name="Star" size={20} color="white" />
                  </div>
                  <h4 className="font-poppins font-semibold text-text-primary">Freshness</h4>
                  <p className="text-text-secondary font-inter text-sm">
                    Delivering moments of pure refreshment
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BrandStorySection;